// The pure side of the video upscaler: which sizes a video can be made, how it will look, how big the file will be. No drawing here.

export const MAX_LONG_SIDE = 3840;                 // 4K UHD
export const MAX_SHORT_SIDE = 2160;
export const MAX_FILE_BYTES = 2 * 1024 * 1024 * 1024;
export const MAX_OUTPUT_BYTES = 1.2e9;             // the finished video is kept in memory until it is saved
export const LONG_VIDEO_SECONDS = 600;

// How the picture is treated: `sharp` (0..1) = how crisp the edges become, `denoise` (0..1) = how much grain and compression blocks are smoothed first.
export const LOOKS = {
  natural: { label: "Natural", about: "A clean, faithful result for most videos.", sharp: 0.35, denoise: 0.15 },
  crisp: { label: "Crisp", about: "Stronger edges and detail - good for screen recordings, animation and text.", sharp: 0.7, denoise: 0.1 },
  restore: { label: "Restore old or noisy video", about: "Smooths grain and compression blocks before enlarging.", sharp: 0.45, denoise: 0.6 },
  custom: { label: "Custom", about: "Set the two sliders yourself.", sharp: 0.4, denoise: 0.2 },
};
export const LOOK_IDS = Object.keys(LOOKS);

const even = (n) => Math.max(2, Math.round(n / 2) * 2);

// The size when the shorter side is made `short` pixels long (the picture keeps its shape).
export function sizeForShortSide(w, h, short) {
  const k = short / Math.min(w, h);
  return [even(w * k), even(h * k)];
}

export const withinLimits = (w, h) => Math.max(w, h) <= MAX_LONG_SIDE && Math.min(w, h) <= MAX_SHORT_SIDE;

// The sizes this video can be made, smallest first: [{ id, label, w, h, factor }]. Only sizes that are really bigger than the video (and that fit 4K) are offered,
// plus "same size" for cleaning a video up without enlarging it.
export function targetOptions(w, h) {
  const short = Math.min(w, h);
  const out = [{ id: "same", label: "Same size", w: even(w), h: even(h), factor: 1 }];
  const seen = new Set([`${even(w)}x${even(h)}`]);
  const add = (id, label, size) => {
    const key = size.join("x");
    if (seen.has(key) || !withinLimits(size[0], size[1]) || Math.min(...size) < short * 1.12) return;
    seen.add(key);
    out.push({ id, label, w: size[0], h: size[1], factor: Math.min(size[0] / w, size[1] / h) });
  };
  [[720, "HD 720p"], [1080, "Full HD 1080p"], [1440, "2K 1440p"], [2160, "4K 2160p"]].forEach(([s, name]) => add(`p${s}`, name, sizeForShortSide(w, h, s)));
  [2, 3, 4].forEach((k) => add(`x${k}`, `${k}× bigger`, [even(w * k), even(h * k)]));
  return out.sort((a, b) => a.w * a.h - b.w * b.h);
}

// A sensible bit rate for the recording of a picture this big.
export function bitrateFor(w, h, fps = 30) {
  return Math.round(Math.min(40e6, Math.max(2.5e6, w * h * fps * 0.1)));
}

export const estimateBytes = (w, h, fps, seconds) => (bitrateFor(w, h, fps) * seconds) / 8;

// What is wrong with doing this job, in plain words, or "" when all is well.
export function problemWith({ w, h, seconds, fps = 30, bytes = 0 }) {
  if (!w || !h) return "This video could not be read.";
  if (bytes > MAX_FILE_BYTES) return "This file is larger than 2 GB. Cut it into shorter parts first.";
  if (!withinLimits(w, h)) return "The new size is above 4K, which browsers cannot record reliably.";
  if (estimateBytes(w, h, fps, seconds) > MAX_OUTPUT_BYTES) return "At this size the finished video would be larger than about 1.2 GB. Choose a smaller size or cut the video into parts.";
  return "";
}

export function clockText(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h ? `${h}:${String(m).padStart(2, "0")}` : m}:${String(s % 60).padStart(2, "0")}`;
}

export function sizeText(bytes) {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1000))} KB`;
}

// The settings of a look, with the custom sliders when the look is "custom".
export function settingsOf(look, custom = {}) {
  const base = LOOKS[look] || LOOKS.natural;
  const clamp = (x) => Math.min(1, Math.max(0, Number.isFinite(x) ? x : 0));
  return look === "custom" ? { sharp: clamp(custom.sharp ?? base.sharp), denoise: clamp(custom.denoise ?? base.denoise) } : { sharp: base.sharp, denoise: base.denoise };
}

// Frames per second from the times (media time in seconds) of consecutive frames that were shown; 0 when there is too little to tell.
export function fpsFromTimes(times) {
  const gaps = [];
  for (let i = 1; i < times.length; i += 1) {
    const g = times[i] - times[i - 1];
    if (g > 0.004 && g < 0.2) gaps.push(g);
  }
  if (gaps.length < 5) return 0;
  gaps.sort((a, b) => a - b);
  const fps = 1 / gaps[Math.floor(gaps.length / 2)];
  const common = [24, 25, 30, 48, 50, 60];
  const near = common.filter((c) => Math.abs(c - fps) < 1.2).sort((a, b) => Math.abs(a - fps) - Math.abs(b - fps))[0];
  return near || Math.round(Math.min(60, Math.max(10, fps)));
}
